import { faCogs, faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import equals from "fast-deep-equal";
import React from "react";
import { ButtonGroup, Col, Container, Dropdown, DropdownButton, Row, Table, ToggleButton } from "react-bootstrap";
import { selectFilter, textFilter } from "react-bootstrap-table2-filter";
import { withTranslation } from "react-i18next";
import PaginationTable from "../../../../components/paginationTable/PaginationTable";
import APIErrorHelper from "../../../../helpers/APIErrorHelper";
import SharingStatus from "../../../../helpers/SharingStatus";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import StorageService from "../../../../services/StorageService";
import ExaminationFilesActionCell from "./examinationFilesActionCell/ExaminationFilesActionCell";
import ExaminationFilesSharingStatCell from "./examinationFilesSharingStatusCell/ExaminationFilesSharingStatCell";
import UploadFileDialog from "./uploadFileDialog/UploadFileDialog";
import RunningDownloadDialog from "../../../../components/dialogs/runningDownloadDialog/RunningDownloadDialog";
import PaginationTableFilterToggle
  from "../../../../components/paginationTableFilterToggle/PaginationTableFilterToggle";

class ExaminationFilesTab extends React.Component {
  constructor(props) {
    super(props);
    this.tableId = "examinationFilesTabTable";
    let showFilters = StorageService.tableConfigShowFilters(this.tableId);
    this.state = {
      examinationFiles: [],
      examinationFileIndexMap: new Map(),
      apiError: "",
      dialogApiError: "",
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      runningDownloadDialogShow: false,
      runningDownloadDialogTitle: "",
      runningDownloadDialogProgress: 0
    };

    this.humanReadableFileSize = this.humanReadableFileSize.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.registerEventListener = this.registerEventListener.bind(this);
    this.handleUploadFile = this.handleUploadFile.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onShare = this.onShare.bind(this);
    this.onDownloadExaminationFile = this.onDownloadExaminationFile.bind(this);
  }

  async componentDidMount() {
    // Load data from cache
    const examinationFiles = StorageService.getReferenceType("examinationFiles");
    if (examinationFiles) this.setState({ examinationFiles });
    await this.fetchData();
    this.registerEventListener();
  }

  async componentDidUpdate(prevProps) {
    if (!equals(prevProps.examination, this.props.examination)) {
      await this.fetchData();
    }
  }

  registerEventListener() {
    CamplaCoreService.addEventListener("ExaminationFilesTab", event => {
      if (
        event.model === "examFileStorage" &&
        event.action === "update" &&
        this.state.examinationFileIndexMap.has(event.object._id)
      ) {
        const idx = this.state.examinationFileIndexMap.get(event.object._id);
        const examinationFile = Object.assign(
          {},
          this.state.examinationFiles[idx]
        );
        examinationFile["sharingStatus"] = event.object.sharingStatus;
        examinationFile["isShared"] = event.object.isShared;
        const examinationFiles = [...this.state.examinationFiles];
        examinationFiles[idx] = examinationFile;
        this.setState({ examinationFiles: examinationFiles });
      }
    });
  }

  async fetchData() {
    if (!this.props.examination) return;
    try {
      const examinationFiles = await CamplaCoreService.getAllExaminationFiles(
        this.props.examination._id
      );
      StorageService.set("examinationFiles", examinationFiles);
      const examinationFileIndexMap = new Map();
      examinationFiles.forEach((ef, idx) =>
        examinationFileIndexMap.set(ef._id, idx)
      );
      this.setState({
        examinationFiles,
        examinationFileIndexMap
      });
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        )
      });
    }
  }

  humanReadableFileSize(size) {
    const labels = ["B", "KB", "MB", "GB"];
    let ct = 0;
    while (size > 1024 && ct < labels.length) {
      size /= 1024;
      ct++;
    }
    return `${Math.round(size * 100) / 100} ${labels[ct]}`;
  }

  async handleUploadFile(files) {
    if (!this.props.examination) return [];
    try {
      const newExaminationFiles = [];
      for (const idx in files) {
        try {
          newExaminationFiles.push(
            await CamplaCoreService.uploadExaminationFile(
              this.props.examination._id,
              files[idx]
            )
          );
          files[idx]["status"] = 1;
        } catch (err) {
          files[idx]["status"] = 2;
          this.setState({
            dialogApiError: APIErrorHelper.getError(
              err,
              () => this.setState({ dialogApiError: "" }),
              {
                PRECONDITION_FAILED:
                  "SingleExamination.ExaminationFilesTab.APIError.PreconditionFailed",
                NOT_FOUND:
                  "SingleExamination.ExaminationFilesTab.APIError.NotFound"
              },
              this.props.t
            )
          });
        }
      }

      const updatedExaminationFiles = [...this.state.examinationFiles];
      newExaminationFiles.forEach(ef => {
        if (!this.state.examinationFileIndexMap.has(ef._id)) updatedExaminationFiles.push(ef);
      });

      const examinationFileIndexMap = new Map();
      updatedExaminationFiles.forEach((ef, idx) => examinationFileIndexMap.set(ef._id, idx));

      this.setState({
        examinationFileIndexMap: examinationFileIndexMap,
        examinationFiles: updatedExaminationFiles
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.ExaminationFilesTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.ExaminationFilesTab.APIError.NotFound"
          },
          this.props.t
        )
      });
    }
    return files;
  }

  async onDelete(file) {
    try {
      await CamplaCoreService.deleteExaminationFile(
        this.props.examination._id,
        file._id
      );
      const examinationFileIndexMap = new Map();
      const examinationFiles = this.state.examinationFiles.filter(
        ef => ef._id !== file._id
      );
      examinationFiles.forEach((ef, idx) =>
        examinationFileIndexMap.set(ef._id, idx)
      );
      this.setState({
        examinationFiles: examinationFiles,
        examinationFileIndexMap: examinationFileIndexMap
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.ExaminationFilesTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.ExaminationFilesTab.APIError.NotFound"
          },
          this.props.t
        )
      });
    }
  }

  async onShare(file, status) {
    try {
      await CamplaCoreService.updateExaminationFile(
        this.props.examination._id,
        file._id,
        { isShared: status }
      );
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.ExaminationFilesTab.APIError.PreconditionFailed",
            NOT_FOUND: "SingleExamination.ExaminationFilesTab.APIError.NotFound"
          },
          this.props.t
        )
      });
    }
  }

  async onDownloadExaminationFile(examinationFile) {
    try {
      this.setState({
        runningDownloadDialogShow: true,
        runningDownloadDialogTitle: examinationFile.name,
        runningDownloadDialogProgress: 0
      });
      await CamplaCoreService.downloadOneExaminationFile(
          examinationFile.examinationId,
          examinationFile._id,
          examinationFile.name,
          percentage =>
              this.setState({ runningDownloadDialogProgress: percentage })
      );
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0
      });
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0,
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {
              PRECONDITION_FAILED:
                  "SingleExamination.ExaminationFilesTab.APIError.PreconditionFailed",
              NOT_FOUND: "SingleExamination.ExaminationFilesTab.APIError.NotFound",
              BAD_REQUEST: "SingleExamination.ExaminationFilesTab.BadRequest"
            },
            this.props.t
        )
      });
    }
  }

  render() {
    const { t } = this.props;
    const actionCellData = {
      examinationStatus: this.props.examination.status
    };
    const columns = [
      {
        dataField: "name",
        text: t("SingleExamination.ExaminationFilesTab.ColumnName"),
        sort: true,
        filter: textFilter({
          placeholder: t(
            "SingleExamination.ExaminationFilesTab.FilterNamePlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "size",
        text: t("SingleExamination.ExaminationFilesTab.ColumnSize"),
        sort: true,
        formatter: cell => <p>{this.humanReadableFileSize(cell)}</p>
      },
      {
        dataField: "sharingStatus",
        text: t("SingleExamination.ExaminationFilesTab.ColumnSharingStatus"),
        sort: true,
        filter: selectFilter({
          options: SharingStatus.getStatus().map(s => ({
            value: s.code,
            label: t(s.translation)
          })),
          placeholder: t(
            "SingleExamination.ExaminationFilesTab.FilterSharingStatusPlaceholder"
          ),
          style: { display: this.state.displayFilter }
        }),
        formatter: cell => <ExaminationFilesSharingStatCell code={cell} />
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
            <PaginationTableFilterToggle
                onFilterToggle={(e) => {
                  this.setState({
                    showFilters: e.currentTarget.checked,
                    displayFilter: e.currentTarget.checked ? "initial" : "none",
                  });
                }}
                showFilters={this.state.showFilters}
                tableId={this.tableId} />
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatExtraData: actionCellData,
        formatter: (cell, row, idx, props) => (
          <ExaminationFilesActionCell
            onDelete={() => this.onDelete(row)}
            onShare={() => this.onShare(row, true)}
            onStopSharing={() => this.onShare(row, false)}
            onDownload={() => this.onDownloadExaminationFile(row)}
            file={row}
            examinationStatus={props.examinationStatus}
          />
        )
      }
    ];
    //TODO: Setup filterable/sortable table, examination file actions (handle them here), needed confirm dialogs
    //TODO: Actions are: Share / Stop sharing / Delete
    //TODO: Actions only possible if the examination is in state ready
    return (
      <div>
        <UploadFileDialog
          show={this.state.showUploadFileModal}
          examination={this.props.examination}
          dialogApiError={this.state.dialogApiError}
          onHide={() => this.setState({ showUploadFileModal: false })}
          onSubmit={this.handleUploadFile}
        />
        <RunningDownloadDialog
            show={this.state.runningDownloadDialogShow}
            onHide={() => this.setState({ runningDownloadDialogShow: false })}
            title={this.state.runningDownloadDialogTitle}
            progress={this.state.runningDownloadDialogProgress}
        />
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row className="mt-3">
            <Col>
              <h4>{t("SingleExamination.ExaminationFilesTabTitle")}</h4>
            </Col>
            <Col>
              <div style={{ float: "right" }}>
                <DropdownButton
                  onClick={event => event.stopPropagation()}
                  disabled={
                    this.props.examination.status !== 1 &&
                    !(
                      this.props.examination.status >= 50 &&
                      this.props.examination.status <= 53
                    )
                  }
                  id="dropdown-basic-button"
                  title={<FontAwesomeIcon icon={faCogs} />}
                  className="btn-green"
                >
                  <Dropdown.Item
                    onClick={() => this.setState({ showUploadFileModal: true })}
                  >
                    {t("SingleExamination.ExaminationFilesTab.UploadButton")}
                  </Dropdown.Item>
                </DropdownButton>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Table size="sm" className="mt-2" bordered>
                <thead>
                <tr>
                  <th>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.ColumnFolder"
                    )}
                  </th>
                  <th>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.ColumnShare"
                    )}
                  </th>
                  <th>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.ColumnAccess"
                    )}
                  </th>
                  <th>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.ColumnProperties"
                    )}
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Documents.ColumnFolder"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Documents.ColumnShare"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Documents.ColumnAccess"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Documents.ColumnProperties"
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Submissions.ColumnFolder"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Submissions.ColumnShare"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Submissions.ColumnAccess"
                    )}
                  </td>
                  <td>
                    {t(
                      "SingleExamination.ExaminationFilesTab.InfoTable.Submissions.ColumnProperties"
                    )}
                  </td>
                </tr>
                </tbody>
              </Table>
              <p>{t("SingleExamination.ExaminationFilesTab.InfoText")}</p>
              <p><b>{t("SingleExamination.ExaminationFilesTab.InfoTextExaminationZip")}</b></p>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <PaginationTable
                data={this.state.examinationFiles}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                id={this.tableId}
                noDataFoundLabel={t(
                    "SingleExamination.ExaminationFilesTab.NoExaminationFilesFound"
                )}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withTranslation()(ExaminationFilesTab);
